'use client'

import { useEffect, useRef, type RefObject } from 'react'
import { trackEvent } from '@/lib/analytics'
import { useCoachMarks } from './use-coach-marks'
import { CoachMarkTooltip } from './coach-mark-tooltip'
import { useDemoSession } from '../use-demo-session-tracking'

interface CoachMarkProviderProps {
  containerRef: RefObject<HTMLDivElement | null>
  view: 'audience' | 'artist'
}

export function CoachMarkProvider({ containerRef, view }: CoachMarkProviderProps) {
  const { activeCoachMark, hasNext, dismiss, next } = useCoachMarks()
  const demoSession = useDemoSession()
  const lastTrackedId = useRef<string | null>(null)

  // Track when a coach mark is shown
  useEffect(() => {
    if (activeCoachMark && activeCoachMark.id !== lastTrackedId.current) {
      if (activeCoachMark.view === view || activeCoachMark.view === 'both') {
        trackEvent('demo_coach_mark_shown', { mark_id: activeCoachMark.id })
        demoSession?.trackMilestone('coach_shown', { markId: activeCoachMark.id })
        lastTrackedId.current = activeCoachMark.id
      }
    }
  }, [activeCoachMark, view, demoSession])

  // Only render if the active mark matches this view (or is 'both')
  if (!activeCoachMark) return null
  if (activeCoachMark.view !== view && activeCoachMark.view !== 'both') return null

  return (
    <CoachMarkTooltip
      coachMark={activeCoachMark}
      hasNext={hasNext}
      onDismiss={() => {
        trackEvent('demo_coach_mark_dismissed', { mark_id: activeCoachMark.id })
        demoSession?.trackMilestone('coach_skipped', { markId: activeCoachMark.id })
        dismiss(activeCoachMark.id)
      }}
      onNext={() => {
        trackEvent('demo_coach_mark_dismissed', { mark_id: activeCoachMark.id, action: 'next' })
        demoSession?.trackMilestone('coach_dismissed', { markId: activeCoachMark.id })
        next(activeCoachMark.id)
      }}
      containerRef={containerRef}
    />
  )
}
